import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { from } from "rxjs";
import { exhaustMap, map, catchError, tap, mergeMap } from "rxjs/operators";
import { GradeService } from "src/app/shared/services/grades/grades.service";
import { AppState } from "../../reducers";
import * as GradeActionTypes from "../../actions/grades/grades.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

@Injectable()
export class GradesEffects {
  getGrades$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.getGradeList),
      exhaustMap((action) =>
        this.gradeService.getGrades(action.pageReq).pipe(
          map((result) => GradeActionTypes.loadGradeList({ response: result })),
          catchError((error) => {
            const actions = [
              GradeActionTypes.loadGradeList({ response: [] }),
              MessagesActionTypes.setMessage(
                this.buildMessageObj("error", "error", "Grades.loadGradesError")
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  getGradesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.getGradesList),
      exhaustMap((action) =>
        this.gradeService
          .getGradeList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              GradeActionTypes.loadGradesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                GradeActionTypes.loadGradesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Grades.loadGradeListError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getGradeExcelList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.getExcelGradesList),
      exhaustMap((action) =>
        this.gradeService
          .getGradeList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              GradeActionTypes.loadExcelGradesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                GradeActionTypes.loadExcelGradesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Grades.loadGradeExcelListError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getGrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.getGrade),
      exhaustMap((action) =>
        this.gradeService.getGrade(action.code).pipe(
          map((result) => GradeActionTypes.loadGrade({ response: result })),
          catchError((error) => {
            const actions = [
              GradeActionTypes.gradeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Grades.loadGradeError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  createGrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.createGrade),
      exhaustMap((action) =>
        this.gradeService.createGrade(action.grade).pipe(
          mergeMap((result) => [
            GradeActionTypes.resetCreation(),
            GradeActionTypes.getGrade({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Grades.gradeCreated"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              GradeActionTypes.gradeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Grades.gradeCreateError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  updateGrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.updateGrade),
      exhaustMap((action) =>
        this.gradeService.updateGrade(action.grade).pipe(
          map((result) => {
            return GradeActionTypes.getGrade({
              code: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Grades.gradeUpdated"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              GradeActionTypes.gradeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Grades.gradeUpdateError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  // Get Products Table Config
  getGradeTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.getGradesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              GradeActionTypes.loadGradesTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                GradeActionTypes.loadGradesTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("TableConfig.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Products Table Config
  saveGradesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GradeActionTypes.saveGradesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            GradeActionTypes.gradeLoadEnd(),
            MessagesActionTypes.setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              GradeActionTypes.gradeLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("TableConfig.SaveError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private gradeService: GradeService,
    private router: Router,
    private translate: TranslateService,
    private tableConfigService: TableConfigService
  ) {}

  buildMessageObj(key: string, severity: string, message: string) {
    return {
      appMessage: {
        key: key,
        message: this.translate.instant(message),
        owner: "Grades-Management",
        severity: severity,
        timestamp: new Date().getTime(),
      },
    };
  }
}
